import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  Modal,
  CircularProgress,
  Slider,
  Stack,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Divider,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../store";
import { GetTimeString } from "../../utiles";
import { LevelupBoost, DeerUpgrade, ToolUpgrade } from "../../store/user/actions";
import CloseIcon from "@mui/icons-material/Close";
import UpgradeIcon from "@mui/icons-material/Upgrade";
import DiamondIcon from "@mui/icons-material/Diamond";
import InfoIcon from "@mui/icons-material/Info";
import "../Tools.css";

const URI_PREFIX = "https://ipfs.io/ipfs/";

interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
  nft: any; // NFT data to display in the modal
  onConfirm: () => void;
  isUpgrading: boolean;
  type: string;
}

// Custom hook for countdown timer
const useCountdown = (initialTime: number) => {
  const [secondsRemaining, setSecondsRemaining] = useState(initialTime);
  const [isRunning, setIsRunning] = useState(false);

  const start = useCallback(
    (startTime?: number) => {
      setSecondsRemaining(startTime !== undefined ? startTime : initialTime);
      setIsRunning(true);
    },
    [initialTime]
  );

  const stop = useCallback(() => {
    setIsRunning(false);
  }, []);

  useEffect(() => {
    if (!isRunning) return;

    const intervalId = setInterval(() => {
      setSecondsRemaining((prevSeconds) => {
        if (prevSeconds <= 1) {
          clearInterval(intervalId);
          setIsRunning(false);
          return 0;
        }
        return prevSeconds - 1;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [isRunning]);

  return {
    secondsRemaining,
    startCountdown: start,
    stopCountdown: stop,
    isRunning: isRunning,
  };
};

const UpgradeModal: React.FC<UpgradeModalProps> = ({
  open,
  onClose,
  nft,
  onConfirm,
  isUpgrading,
  type = "deer",
}) => {
  const dispatch = useDispatch<any>();
  const userModule = useSelector((state: AppState) => state.userModule);
  const loginInfo = userModule.loginInfo;
  const [loadingUpgrade, setLoadingUpgrade] = useState(false);
  const [loadingBoost, setLoadingBoost] = useState(false);
  const [targetLevel, setTargetLevel] = useState(0);

  // Reset state and directly use nft prop throughout instead of keeping a copy
  // This ensures we always display the currently selected NFT

  const {
    secondsRemaining: uTime,
    startCountdown: startTimer,
    stopCountdown: stopTimer,
  } = useCountdown(0);

  // Reset and initialize state when modal opens or NFT changes
  useEffect(() => {
    // Reset timer when modal closes
    if (!open) {
      stopTimer();
      return;
    }

    // Return early if no NFT data is available
    if (!nft) return;

    // Set target level to next level
    setTargetLevel((parseInt(nft?.level) || 0) + 1);

    // Check upgrade status and start timer if needed
    const currentUnixTime = Math.floor(Date.now() / 1000);
    const delayTime = nft.upgrade_end_at - currentUnixTime;

    if (delayTime > 0) {
      startTimer(delayTime);
    } else {
      stopTimer();
    }
  }, [nft, open, startTimer, stopTimer]);

  // CALCULATE UPGRADE COST
  const calculateUpgradeCost = useCallback(() => {
    if (!nft) return { antlers: 0, gold: 0, delay: 0 };

    let totalAntlers = 0;
    let totalGold = 0;
    let totalXP = 0;
    let totalDelay = 0;

    const deer_config = nft.deer_config || nft.tool_config;
    if (!deer_config) return { antlers: 0, gold: 0, delay: 0, xp: 0 };

    const level_costa = deer_config.level_costa;
    const level_costb = deer_config.level_costb;
    const level_delay = deer_config.level_delay;
    const levelc = parseInt(nft.level) || 1;

    // for()
    const costA = level_costa?.find((a: any) => a.key == levelc + 1);
    const costB = level_costb?.find((a: any) => a.key == levelc + 1);
    const delay = level_delay?.find((a: any) => a.key == levelc + 1);

    if (!costA || !costB || !delay)
      return { antlers: 0, gold: 0, delay: 0, xp: 0 };

    // totalGold += parseFloat(costA.value || "0");
    if (costB.value) {
      const [amountStr, currency = "GOLD"] = costB.value.split(" ");
      const amount = parseFloat(amountStr || "0");
      switch (currency.toUpperCase()) {
        case "GOLD":
          totalGold += amount;
          break;
        case "XP":
          totalXP += amount;
          break;
        default:
          totalGold += amount;
      }
    }

    totalAntlers += parseFloat(costA.value || "0");

    totalDelay += parseFloat(delay.value);

    const delayFormatted = (() => {
      const hours = Math.floor(totalDelay / 3600);
      const minutes = Math.floor((totalDelay % 3600) / 60);
      if (hours > 0 && minutes > 0) return `${hours} hours ${minutes} minutes`;
      if (hours > 0) return `${hours} hours`;
      if (minutes > 0) return `${minutes} minutes`;
      return `${totalDelay}s`;
    })();

    return { antlers: totalAntlers, gold: totalGold, delay: delayFormatted, xp: totalXP };
  }, [nft, targetLevel]);

  const upgradeCost = React.useMemo(
    () => calculateUpgradeCost(),
    [calculateUpgradeCost]
  );

  // CALCULATE BOOST COST
  const calculateBoostCost = useCallback(() => {
    if (!nft) return 0;
    const boostCost = 20;
    return boostCost;
  }, [nft]);

  const boostCost = React.useMemo(
    () => calculateBoostCost(),
    [calculateBoostCost]
  );

  const levelup = useCallback(async () => {
    if (!nft) return;
    setLoadingUpgrade(true);
    try {
      if (type == "tool")
        await dispatch(ToolUpgrade(nft.asset_id));
      else
        await dispatch(DeerUpgrade(nft.asset_id, nft.level, targetLevel));
      onClose();
    } catch (error) {
      console.error("Upgrade failed:", error);
    } finally {
      setLoadingUpgrade(false);
    }
  }, [dispatch, nft, targetLevel, onClose]);

  const levelupboost = useCallback(async () => {
    if (!nft) return;
    setLoadingBoost(true);
    try {
      await dispatch(LevelupBoost(nft.asset_id, 1));
      onClose();
    } catch (error) {
      console.error("Boost failed:", error);
    } finally {
      setLoadingBoost(false);
    }
  }, [dispatch, nft, onClose]);

  if (!nft) return null;

  const maxLevel = nft.deer_config?.level_costa?.length
    ? nft.deer_config.level_costa.length + 1
    : nft.level + 5;

  const modalStyle = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "90%", sm: 400 },
    bgcolor: "#1a203c",
    color: "white",
    border: "4px solid #ea923e",
    boxShadow: 24,
    textAlign: "center",
    borderRadius: "16px",
    p: 4,
    maxHeight: "90vh",
    overflow: "auto",
  };

  const calculateStats = () => {
    if (!nft) return null;
    const currentLevel = parseInt(nft?.level) || 1;
    const nextLevel = currentLevel + 1;

    return {
      currentLevel,
      nextLevel,
      upgradeCost
    };
  };

  const stats = calculateStats();

  const getRarityColor = (rarity: string) => {
    switch (rarity.toLowerCase()) {
      case "common": return "#B0B0B0";
      case "uncommon": return "#1BBE5B";
      case "rare": return "#4990E2";
      case "epic": return "#9013FE";
      case "legendary": return "#FFC700";
      default: return "#B0B0B0";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          background: "linear-gradient(135deg, #3a2609 0%, #5c3c10 100%)",
          borderRadius: "16px",
          border: "4px solid #f5e1a3",
          position: "relative",
          overflow: "hidden",
        }
      }}
    >
      <Box className="wood-texture-overlay" />

      <DialogTitle
        sx={{
          color: "#f5e1a3",
          fontWeight: "bold",
          textAlign: "center",
          fontSize: "1.8rem",
          textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
          paddingBottom: 1,
          position: "relative"
        }}
      >
        <UpgradeIcon sx={{ verticalAlign: "middle", marginRight: 1 }} />
        Upgrade NFT
        <IconButton
          onClick={onClose}
          disabled={isUpgrading}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "#f5e1a3",
            "&:hover": {
              backgroundColor: "rgba(245, 225, 163, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider sx={{
        borderColor: "rgba(245, 225, 163, 0.3)",
        width: "90%",
        margin: "0 auto",
      }} />

      <DialogContent sx={{ padding: 3, position: "relative" }}>
        {nft ? (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 3,
                alignItems: "stretch",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  mb: { xs: 2, md: 0 },
                  minWidth: 0,
                }}
              >
                <Paper
                  elevation={3}
                  sx={{
                    padding: 2,
                    backgroundColor: "rgba(0, 0, 0, 0.3)",
                    border: "2px solid #7d5014",
                    borderRadius: "12px",
                    overflow: "hidden",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 220,
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      backgroundImage: `url(${URI_PREFIX + nft.data?.img})`,
                      backgroundSize: "contain",
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                      borderRadius: "8px",
                      aspectRatio: "1 / 1",
                      flex: 1,
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#f5e1a3",
                      fontWeight: "bold",
                      textAlign: "center",
                      mt: 2,
                      fontSize: { xs: "1.2rem", md: "1.5rem" },
                    }}
                  >
                    {nft.name}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#f5e1a3",
                      fontWeight: "bold",
                      textAlign: "center",
                      mt: 2,
                      fontSize: "0.9rem",
                    }}
                  >
                    #{nft.asset_id}
                  </Typography>
                </Paper>
              </Box>

              <Box
                sx={{
                  flex: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  minWidth: 0,
                }}
              >
                <Paper
                  elevation={3}
                  sx={{
                    padding: 2,
                    backgroundColor: "rgba(245, 225, 163, 0.05)",
                    border: "2px solid #7d5014",
                    borderRadius: "12px",
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <InfoIcon sx={{ color: "#f5e1a3", mr: 1 }} />
                    <Typography variant="subtitle1" sx={{ color: "#f5e1a3", fontWeight: "bold" }}>
                      Upgrade Cost
                    </Typography>
                  </Box>

                  <Typography variant="body2" sx={{ color: "white", mb: 2 }}>
                    Upgrading from Level {stats?.currentLevel} to Level {stats?.nextLevel} will cost:
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 1.5,
                      backgroundColor: "rgba(0, 0, 0, 0.2)",
                      borderRadius: "8px",
                      border: "1px solid rgba(245, 225, 163, 0.2)",
                      mb: 1,
                    }}
                  >
                    {
                      type == "tool" && stats?.upgradeCost && (stats.upgradeCost.xp ?? 0) > 0 ? (
                        <>
                          <img
                            src={'/images/xp.webp'}
                            alt="XP"
                            style={{ width: 28, height: 28, marginRight: 8, verticalAlign: "middle" }}
                          />
                          <Typography variant="h5" sx={{ color: "#f5e1a3", fontWeight: "bold" }}>
                            {stats.upgradeCost.xp} XP
                          </Typography>
                        </>
                      ) : (
                        <>
                          <img
                            src={'/images/token_gold.png'}
                            alt="Gold"
                            style={{ width: 28, height: 28, marginRight: 8, verticalAlign: "middle" }}
                          />
                          <Typography variant="h5" sx={{ color: "#f5e1a3", fontWeight: "bold" }}>
                            {stats?.upgradeCost?.gold} GOLD
                          </Typography>
                        </>
                      )
                    }
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 1.5,
                      backgroundColor: "rgba(0, 0, 0, 0.2)",
                      borderRadius: "8px",
                      border: "1px solid rgba(245, 225, 163, 0.2)",
                    }}
                  >
                    <img
                      src={'/images/token_antlers.png'}
                      alt="Antler"
                      style={{ width: 28, height: 28, marginRight: 8, verticalAlign: "middle" }}
                    />
                    <Typography variant="h5" sx={{ color: "#f5e1a3", fontWeight: "bold" }}>
                      {stats?.upgradeCost?.antlers} ANTLERS
                    </Typography>
                  </Box>
                </Paper>
                <Paper
                  elevation={3}
                  sx={{
                    padding: 2,
                    backgroundColor: "rgba(245, 225, 163, 0.05)",
                    border: "2px solid #7d5014",
                    borderRadius: "12px",
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <InfoIcon sx={{ color: "#f5e1a3", mr: 1 }} />
                    <Typography variant="subtitle1" sx={{ color: "#f5e1a3", fontWeight: "bold" }}>
                      Time to Upgrade
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: "white" }}>
                    The upgrade will take: {stats?.upgradeCost?.delay}
                  </Typography>
                </Paper>
              </Box>
            </Box>

            <Box
              sx={{
                mt: 3,
                p: 2,
                backgroundColor: "rgba(0, 0, 0, 0.2)",
                borderRadius: "8px",
                border: "1px solid rgba(245, 225, 163, 0.2)"
              }}
            >
              <Typography variant="body2" sx={{ color: "#f5e1a3", fontStyle: "italic" }}>
                <InfoIcon sx={{ fontSize: "0.9rem", verticalAlign: "middle", mr: 0.5 }} />
                Note: Upgrades are permanent and cannot be reversed. Each level upgrade will provide a bonus in tournament odds.
              </Typography>
            </Box>
          </>
        ) : (
          <Typography variant="body1" color="error">
            NFT data not available
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: "16px 24px" }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isUpgrading}
          sx={{
            color: "#f5e1a3",
            borderColor: "#f5e1a3",
            '&:hover': {
              borderColor: "white",
              backgroundColor: "rgba(245, 225, 163, 0.1)"
            }
          }}
        >
          Cancel
        </Button>
        <Button
          variant="outlined"
          onClick={levelup}
          disabled={isUpgrading}
          sx={{
            color: "#f5e1a3",
            borderColor: "#f5e1a3",
            '&:hover': {
              borderColor: "white",
              backgroundColor: "rgba(245, 225, 163, 0.1)"
            }
          }}
        >
          {isUpgrading ? (
            <CircularProgress size={24} sx={{ color: "#f5e1a3" }} />
          ) : (
            "Upgrade"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpgradeModal;
